'use client';

/**
 * WebGL fluid simulation that follows the cursor. Adapted from PavelDoGreat's
 * webgl-fluid-simulation, with the colour palette constrained to Wantek's
 * cyan/blue range and the demo UI stripped out.
 *
 * Quality gate: the effect bails out on `low` tier (mobile, reduced-motion,
 * weak hardware) — the QualityProvider already encodes that policy.
 */

import { useEffect, useRef } from 'react';
import { useQuality } from '@/components/shared/QualityProvider';

type Color = { r: number; g: number; b: number };

type Pointer = {
  id: number;
  texcoordX: number;
  texcoordY: number;
  prevTexcoordX: number;
  prevTexcoordY: number;
  deltaX: number;
  deltaY: number;
  down: boolean;
  moved: boolean;
  color: Color;
};

type GL = WebGL2RenderingContext | WebGLRenderingContext;

type Format = { internalFormat: number; format: number } | null;

type Ext = {
  formatRGBA: Format;
  formatRG: Format;
  formatR: Format;
  halfFloatTexType: number;
  supportLinearFiltering: boolean;
};

type FBO = {
  texture: WebGLTexture;
  fbo: WebGLFramebuffer;
  width: number;
  height: number;
  texelSizeX: number;
  texelSizeY: number;
  attach: (id: number) => number;
};

type DoubleFBO = {
  width: number;
  height: number;
  texelSizeX: number;
  texelSizeY: number;
  read: FBO;
  write: FBO;
  swap: () => void;
};

type Uniforms = Record<string, WebGLUniformLocation | null>;

const CONFIG = {
  SIM_RESOLUTION: 128,
  DYE_RESOLUTION: 1024,
  // Higher dissipation = smoke evaporates sooner. Tuned for ambient feel.
  DENSITY_DISSIPATION: 5.5,
  VELOCITY_DISSIPATION: 2.5,
  PRESSURE: 0.1,
  PRESSURE_ITERATIONS: 20,
  CURL: 3,
  SPLAT_RADIUS: 0.15,
  SPLAT_FORCE: 5000,
  SHADING: true,
  // Slow cycle through the cyan-blue band so colour barely shifts.
  COLOR_UPDATE_SPEED: 2,
};

// Wantek palette anchors (#316380 ≈ 204°, #66bbcd ≈ 191°). Constrain the random
// hue to a tight cyan/blue band so splats stay on-brand instead of going rainbow.
const HUE_MIN = 0.5;
const HUE_RANGE = 0.08;
// Whisper-level luminance — we want the smoke to read as atmosphere, not splash.
const COLOR_INTENSITY = 0.08;

export function SmokeyCursorEffect() {
  const { tier } = useQuality();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const enabled = tier === 'high' || tier === 'mid';

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const cleanup = startFluidSimulation(canvas);
    return cleanup;
  }, [enabled]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-40 block h-full w-full"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Simulation. Everything below is framework-agnostic and runs inside the effect.
// ─────────────────────────────────────────────────────────────────────────────

function startFluidSimulation(canvas: HTMLCanvasElement): () => void {
  const params = {
    alpha: true,
    depth: false,
    stencil: false,
    antialias: false,
    preserveDrawingBuffer: false,
  };

  const glCandidate =
    (canvas.getContext('webgl2', params) as WebGL2RenderingContext | null) ??
    (canvas.getContext('webgl', params) as WebGLRenderingContext | null) ??
    (canvas.getContext('experimental-webgl', params) as WebGLRenderingContext | null);

  if (!glCandidate) return () => {};
  const gl: GL = glCandidate;

  const isWebGL2 = 'drawBuffers' in gl;
  let supportLinearFiltering = false;
  let halfFloatTexType = 0;

  if (isWebGL2) {
    const gl2 = gl as WebGL2RenderingContext;
    gl2.getExtension('EXT_color_buffer_float');
    supportLinearFiltering = !!gl2.getExtension('OES_texture_float_linear');
    halfFloatTexType = gl2.HALF_FLOAT;
  } else {
    const halfFloat = gl.getExtension('OES_texture_half_float');
    supportLinearFiltering = !!gl.getExtension('OES_texture_half_float_linear');
    halfFloatTexType = halfFloat?.HALF_FLOAT_OES ?? 0;
  }

  gl.clearColor(0, 0, 0, 1);

  let formatRGBA: Format;
  let formatRG: Format;
  let formatR: Format;

  if (isWebGL2) {
    const gl2 = gl as WebGL2RenderingContext;
    formatRGBA = pickFormat(gl, gl2.RGBA16F, gl2.RGBA, halfFloatTexType);
    formatRG = pickFormat(gl, gl2.RG16F, gl2.RG, halfFloatTexType);
    formatR = pickFormat(gl, gl2.R16F, gl2.RED, halfFloatTexType);
  } else {
    formatRGBA = pickFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
    formatRG = pickFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
    formatR = pickFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
  }

  const ext: Ext = { formatRGBA, formatRG, formatR, halfFloatTexType, supportLinearFiltering };

  // On weaker GPUs that lack linear filtering, drop dye resolution and shading
  // to keep frame times under control.
  const dyeResolution = ext.supportLinearFiltering ? CONFIG.DYE_RESOLUTION : 256;
  const useShading = ext.supportLinearFiltering && CONFIG.SHADING;

  if (!formatRGBA) return () => {};

  // ── Shaders ───────────────────────────────────────────────────────────────

  const baseVertex = compileShader(gl, gl.VERTEX_SHADER, BASE_VERTEX_SRC);
  const copyFrag = compileShader(gl, gl.FRAGMENT_SHADER, COPY_FRAG_SRC);
  const clearFrag = compileShader(gl, gl.FRAGMENT_SHADER, CLEAR_FRAG_SRC);
  const splatFrag = compileShader(gl, gl.FRAGMENT_SHADER, SPLAT_FRAG_SRC);
  const advectionFrag = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    ADVECTION_FRAG_SRC,
    ext.supportLinearFiltering ? null : ['MANUAL_FILTERING'],
  );
  const divergenceFrag = compileShader(gl, gl.FRAGMENT_SHADER, DIVERGENCE_FRAG_SRC);
  const curlFrag = compileShader(gl, gl.FRAGMENT_SHADER, CURL_FRAG_SRC);
  const vorticityFrag = compileShader(gl, gl.FRAGMENT_SHADER, VORTICITY_FRAG_SRC);
  const pressureFrag = compileShader(gl, gl.FRAGMENT_SHADER, PRESSURE_FRAG_SRC);
  const gradientSubFrag = compileShader(gl, gl.FRAGMENT_SHADER, GRADIENT_SUB_FRAG_SRC);
  const displayFrag = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    DISPLAY_FRAG_SRC,
    useShading ? ['SHADING'] : null,
  );

  if (
    !baseVertex || !copyFrag || !clearFrag || !splatFrag || !advectionFrag ||
    !divergenceFrag || !curlFrag || !vorticityFrag || !pressureFrag ||
    !gradientSubFrag || !displayFrag
  ) {
    return () => {};
  }

  const programs = {
    copy: makeProgram(gl, baseVertex, copyFrag),
    clear: makeProgram(gl, baseVertex, clearFrag),
    splat: makeProgram(gl, baseVertex, splatFrag),
    advection: makeProgram(gl, baseVertex, advectionFrag),
    divergence: makeProgram(gl, baseVertex, divergenceFrag),
    curl: makeProgram(gl, baseVertex, curlFrag),
    vorticity: makeProgram(gl, baseVertex, vorticityFrag),
    pressure: makeProgram(gl, baseVertex, pressureFrag),
    gradient: makeProgram(gl, baseVertex, gradientSubFrag),
    display: makeProgram(gl, baseVertex, displayFrag),
  };

  if (Object.values(programs).some((p) => p === null)) return () => {};

  // ── Geometry blit ─────────────────────────────────────────────────────────

  const vertexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(0);

  function blit(target: FBO | null, doClear = false) {
    if (!target) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    } else {
      gl.viewport(0, 0, target.width, target.height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    }
    if (doClear) {
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  }

  // ── Framebuffers ──────────────────────────────────────────────────────────

  let dye: DoubleFBO | null = null;
  let velocity: DoubleFBO | null = null;
  let divergenceFBO: FBO;
  let curlFBO: FBO;
  let pressureFBO: DoubleFBO;

  function initFramebuffers() {
    const sim = getResolution(gl, CONFIG.SIM_RESOLUTION);
    const dyeRes = getResolution(gl, dyeResolution);
    const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST;
    gl.disable(gl.BLEND);

    const rgba = ext.formatRGBA!;
    const rg = ext.formatRG ?? rgba;
    const r = ext.formatR ?? rgba;

    if (!dye) {
      dye = createDoubleFBO(gl, dyeRes.width, dyeRes.height, rgba.internalFormat, rgba.format, halfFloatTexType, filtering);
    }
    if (!velocity) {
      velocity = createDoubleFBO(gl, sim.width, sim.height, rg.internalFormat, rg.format, halfFloatTexType, filtering);
    }
    divergenceFBO = createFBO(gl, sim.width, sim.height, r.internalFormat, r.format, halfFloatTexType, gl.NEAREST);
    curlFBO = createFBO(gl, sim.width, sim.height, r.internalFormat, r.format, halfFloatTexType, gl.NEAREST);
    pressureFBO = createDoubleFBO(gl, sim.width, sim.height, r.internalFormat, r.format, halfFloatTexType, gl.NEAREST);
  }

  initFramebuffers();

  // ── State ─────────────────────────────────────────────────────────────────

  const pointer: Pointer = {
    id: -1,
    texcoordX: 0,
    texcoordY: 0,
    prevTexcoordX: 0,
    prevTexcoordY: 0,
    deltaX: 0,
    deltaY: 0,
    down: false,
    moved: false,
    color: brandColor(),
  };

  let lastUpdateTime = performance.now();
  let colorUpdateTimer = 0;
  let rafId = 0;

  // ── Simulation step ───────────────────────────────────────────────────────

  function step(dt: number) {
    if (!velocity || !dye) return;

    gl.disable(gl.BLEND);

    // Curl
    const curl = programs.curl!;
    gl.useProgram(curl.program);
    gl.uniform2f(curl.uniforms.texelSize!, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(curl.uniforms.uVelocity!, velocity.read.attach(0));
    blit(curlFBO);

    // Vorticity
    const vort = programs.vorticity!;
    gl.useProgram(vort.program);
    gl.uniform2f(vort.uniforms.texelSize!, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(vort.uniforms.uVelocity!, velocity.read.attach(0));
    gl.uniform1i(vort.uniforms.uCurl!, curlFBO.attach(1));
    gl.uniform1f(vort.uniforms.curl!, CONFIG.CURL);
    gl.uniform1f(vort.uniforms.dt!, dt);
    blit(velocity.write);
    velocity.swap();

    // Divergence
    const div = programs.divergence!;
    gl.useProgram(div.program);
    gl.uniform2f(div.uniforms.texelSize!, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(div.uniforms.uVelocity!, velocity.read.attach(0));
    blit(divergenceFBO);

    // Clear pressure
    const clr = programs.clear!;
    gl.useProgram(clr.program);
    gl.uniform1i(clr.uniforms.uTexture!, pressureFBO.read.attach(0));
    gl.uniform1f(clr.uniforms.value!, CONFIG.PRESSURE);
    blit(pressureFBO.write);
    pressureFBO.swap();

    // Pressure
    const pres = programs.pressure!;
    gl.useProgram(pres.program);
    gl.uniform2f(pres.uniforms.texelSize!, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(pres.uniforms.uDivergence!, divergenceFBO.attach(0));
    for (let i = 0; i < CONFIG.PRESSURE_ITERATIONS; i++) {
      gl.uniform1i(pres.uniforms.uPressure!, pressureFBO.read.attach(1));
      blit(pressureFBO.write);
      pressureFBO.swap();
    }

    // Gradient subtract
    const grad = programs.gradient!;
    gl.useProgram(grad.program);
    gl.uniform2f(grad.uniforms.texelSize!, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(grad.uniforms.uPressure!, pressureFBO.read.attach(0));
    gl.uniform1i(grad.uniforms.uVelocity!, velocity.read.attach(1));
    blit(velocity.write);
    velocity.swap();

    // Advect velocity
    const adv = programs.advection!;
    gl.useProgram(adv.program);
    gl.uniform2f(adv.uniforms.texelSize!, velocity.texelSizeX, velocity.texelSizeY);
    if (!ext.supportLinearFiltering) {
      gl.uniform2f(adv.uniforms.dyeTexelSize!, velocity.texelSizeX, velocity.texelSizeY);
    }
    const velocityId = velocity.read.attach(0);
    gl.uniform1i(adv.uniforms.uVelocity!, velocityId);
    gl.uniform1i(adv.uniforms.uSource!, velocityId);
    gl.uniform1f(adv.uniforms.dt!, dt);
    gl.uniform1f(adv.uniforms.dissipation!, CONFIG.VELOCITY_DISSIPATION);
    blit(velocity.write);
    velocity.swap();

    // Advect dye
    if (!ext.supportLinearFiltering) {
      gl.uniform2f(adv.uniforms.dyeTexelSize!, dye.texelSizeX, dye.texelSizeY);
    }
    gl.uniform1i(adv.uniforms.uVelocity!, velocity.read.attach(0));
    gl.uniform1i(adv.uniforms.uSource!, dye.read.attach(1));
    gl.uniform1f(adv.uniforms.dissipation!, CONFIG.DENSITY_DISSIPATION);
    blit(dye.write);
    dye.swap();
  }

  function render() {
    if (!dye) return;
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);
    const disp = programs.display!;
    gl.useProgram(disp.program);
    if (useShading && disp.uniforms.texelSize) {
      gl.uniform2f(disp.uniforms.texelSize, 1 / gl.drawingBufferWidth, 1 / gl.drawingBufferHeight);
    }
    gl.uniform1i(disp.uniforms.uTexture!, dye.read.attach(0));
    blit(null, false);
  }

  function splat(x: number, y: number, dx: number, dy: number, color: Color) {
    if (!velocity || !dye) return;
    const sp = programs.splat!;
    gl.useProgram(sp.program);
    gl.uniform1i(sp.uniforms.uTarget!, velocity.read.attach(0));
    gl.uniform1f(sp.uniforms.aspectRatio!, canvas.width / canvas.height);
    gl.uniform2f(sp.uniforms.point!, x, y);
    gl.uniform3f(sp.uniforms.color!, dx, dy, 0);
    gl.uniform1f(sp.uniforms.radius!, correctRadius(CONFIG.SPLAT_RADIUS / 100));
    blit(velocity.write);
    velocity.swap();

    gl.uniform1i(sp.uniforms.uTarget!, dye.read.attach(0));
    gl.uniform3f(sp.uniforms.color!, color.r, color.g, color.b);
    blit(dye.write);
    dye.swap();
  }

  function correctRadius(radius: number) {
    const aspect = canvas.width / canvas.height;
    return aspect > 1 ? radius * aspect : radius;
  }

  function resizeCanvas() {
    const pixelRatio = window.devicePixelRatio || 1;
    const w = Math.floor(canvas.clientWidth * pixelRatio);
    const h = Math.floor(canvas.clientHeight * pixelRatio);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      return true;
    }
    return false;
  }

  function frame() {
    const now = performance.now();
    const dt = Math.min((now - lastUpdateTime) / 1000, 0.01666);
    lastUpdateTime = now;

    if (resizeCanvas()) {
      // Resize trashes the FBOs — recreate them at the new resolution.
      dye = null;
      velocity = null;
      initFramebuffers();
    }

    colorUpdateTimer += dt * CONFIG.COLOR_UPDATE_SPEED;
    if (colorUpdateTimer >= 1) {
      colorUpdateTimer %= 1;
      pointer.color = brandColor();
    }

    if (pointer.moved) {
      pointer.moved = false;
      if (now >= startupGraceUntil) {
        const dx = pointer.deltaX * CONFIG.SPLAT_FORCE;
        const dy = pointer.deltaY * CONFIG.SPLAT_FORCE;
        splat(pointer.texcoordX, pointer.texcoordY, dx, dy, pointer.color);
      }
    }

    step(dt);
    render();
    rafId = requestAnimationFrame(frame);
  }

  // ── Pointer wiring ────────────────────────────────────────────────────────

  // Two-stage suppression for reloads:
  //  1. `pointerCalibrated` — first mousemove only records position, never a delta.
  //     Otherwise prev=(0,0) → screen-wide jet on the first real move.
  //  2. `startupGraceUntil` — for ~700ms after mount we accept calibration but
  //     refuse to splat. This swallows the trail the cursor leaves while
  //     drifting from the refresh button to wherever the user actually looks.
  let pointerCalibrated = false;
  const startupGraceUntil = performance.now() + 700;

  function updateMove(posX: number, posY: number) {
    if (canvas.width === 0 || canvas.height === 0) return;
    const ratio = window.devicePixelRatio || 1;
    const newX = (posX * ratio) / canvas.width;
    const newY = 1 - (posY * ratio) / canvas.height;

    if (!pointerCalibrated) {
      pointer.texcoordX = newX;
      pointer.texcoordY = newY;
      pointer.prevTexcoordX = newX;
      pointer.prevTexcoordY = newY;
      pointerCalibrated = true;
      return;
    }

    pointer.prevTexcoordX = pointer.texcoordX;
    pointer.prevTexcoordY = pointer.texcoordY;
    pointer.texcoordX = newX;
    pointer.texcoordY = newY;
    const aspect = canvas.width / canvas.height;
    let dx = pointer.texcoordX - pointer.prevTexcoordX;
    let dy = pointer.texcoordY - pointer.prevTexcoordY;
    if (aspect < 1) dx *= aspect;
    if (aspect > 1) dy /= aspect;
    pointer.deltaX = dx;
    pointer.deltaY = dy;
    pointer.moved = Math.abs(dx) > 0 || Math.abs(dy) > 0;
  }

  function onMouseMove(e: MouseEvent) {
    updateMove(e.clientX, e.clientY);
  }

  function onTouchMove(e: TouchEvent) {
    const t = e.targetTouches[0];
    if (!t) return;
    updateMove(t.clientX, t.clientY);
  }

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('touchmove', onTouchMove, { passive: true });

  rafId = requestAnimationFrame(frame);

  return () => {
    cancelAnimationFrame(rafId);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('touchmove', onTouchMove);
    // Surrender the WebGL context so the GPU resources release on hot-reload.
    const lose = gl.getExtension('WEBGL_lose_context');
    lose?.loseContext();
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function pickFormat(gl: GL, internalFormat: number, format: number, type: number): Format {
  if (!testFormat(gl, internalFormat, format, type)) {
    if ('drawBuffers' in gl) {
      const gl2 = gl as WebGL2RenderingContext;
      switch (internalFormat) {
        case gl2.R16F:
          return pickFormat(gl, gl2.RG16F, gl2.RG, type);
        case gl2.RG16F:
          return pickFormat(gl, gl2.RGBA16F, gl2.RGBA, type);
        default:
          return null;
      }
    }
    return null;
  }
  return { internalFormat, format };
}

function testFormat(gl: GL, internalFormat: number, format: number, type: number): boolean {
  const texture = gl.createTexture();
  if (!texture) return false;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);

  const fbo = gl.createFramebuffer();
  if (!fbo) return false;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
}

function compileShader(
  gl: GL,
  type: number,
  source: string,
  keywords: string[] | null = null,
): WebGLShader | null {
  let src = source;
  if (keywords?.length) {
    src = keywords.map((k) => `#define ${k}\n`).join('') + source;
  }
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
}

function makeProgram(
  gl: GL,
  vertex: WebGLShader,
  fragment: WebGLShader,
): { program: WebGLProgram; uniforms: Uniforms } | null {
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(program));
    return null;
  }
  const uniforms: Uniforms = {};
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i++) {
    const info = gl.getActiveUniform(program, i);
    if (info) uniforms[info.name] = gl.getUniformLocation(program, info.name);
  }
  return { program, uniforms };
}

function createFBO(
  gl: GL,
  w: number,
  h: number,
  internalFormat: number,
  format: number,
  type: number,
  param: number,
): FBO {
  gl.activeTexture(gl.TEXTURE0);
  const texture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null);
  const fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  gl.viewport(0, 0, w, h);
  gl.clear(gl.COLOR_BUFFER_BIT);
  return {
    texture,
    fbo,
    width: w,
    height: h,
    texelSizeX: 1 / w,
    texelSizeY: 1 / h,
    attach(id: number) {
      gl.activeTexture(gl.TEXTURE0 + id);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      return id;
    },
  };
}

function createDoubleFBO(
  gl: GL,
  w: number,
  h: number,
  internalFormat: number,
  format: number,
  type: number,
  param: number,
): DoubleFBO {
  const a = createFBO(gl, w, h, internalFormat, format, type, param);
  const b = createFBO(gl, w, h, internalFormat, format, type, param);
  return {
    width: w,
    height: h,
    texelSizeX: a.texelSizeX,
    texelSizeY: a.texelSizeY,
    read: a,
    write: b,
    swap() {
      const tmp = this.read;
      this.read = this.write;
      this.write = tmp;
    },
  };
}

function getResolution(gl: GL, resolution: number) {
  const w = gl.drawingBufferWidth;
  const h = gl.drawingBufferHeight;
  const aspect = w / h < 1 ? h / w : w / h;
  const min = Math.round(resolution);
  const max = Math.round(resolution * aspect);
  return w > h ? { width: max, height: min } : { width: min, height: max };
}

function brandColor(): Color {
  const h = HUE_MIN + Math.random() * HUE_RANGE;
  return scaleColor(hsvToRgb(h, 1, 1), COLOR_INTENSITY);
}

function scaleColor(c: Color, k: number): Color {
  return { r: c.r * k, g: c.g * k, b: c.b * k };
}

function hsvToRgb(h: number, s: number, v: number): Color {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: return { r: v, g: t, b: p };
    case 1: return { r: q, g: v, b: p };
    case 2: return { r: p, g: v, b: t };
    case 3: return { r: p, g: q, b: v };
    case 4: return { r: t, g: p, b: v };
    default: return { r: v, g: p, b: q };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shader sources
// ─────────────────────────────────────────────────────────────────────────────

const BASE_VERTEX_SRC = `
precision highp float;
attribute vec2 aPosition;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
uniform vec2 texelSize;
void main () {
  vUv = aPosition * 0.5 + 0.5;
  vL = vUv - vec2(texelSize.x, 0.0);
  vR = vUv + vec2(texelSize.x, 0.0);
  vT = vUv + vec2(0.0, texelSize.y);
  vB = vUv - vec2(0.0, texelSize.y);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const COPY_FRAG_SRC = `
precision mediump float;
precision mediump sampler2D;
varying highp vec2 vUv;
uniform sampler2D uTexture;
void main () { gl_FragColor = texture2D(uTexture, vUv); }`;

const CLEAR_FRAG_SRC = `
precision mediump float;
precision mediump sampler2D;
varying highp vec2 vUv;
uniform sampler2D uTexture;
uniform float value;
void main () { gl_FragColor = value * texture2D(uTexture, vUv); }`;

const DISPLAY_FRAG_SRC = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
uniform sampler2D uTexture;
uniform vec2 texelSize;
void main () {
  vec3 c = texture2D(uTexture, vUv).rgb;
  #ifdef SHADING
    vec3 lc = texture2D(uTexture, vL).rgb;
    vec3 rc = texture2D(uTexture, vR).rgb;
    vec3 tc = texture2D(uTexture, vT).rgb;
    vec3 bc = texture2D(uTexture, vB).rgb;
    float dx = length(rc) - length(lc);
    float dy = length(tc) - length(bc);
    vec3 n = normalize(vec3(dx, dy, length(texelSize)));
    vec3 l = vec3(0.0, 0.0, 1.0);
    float diffuse = clamp(dot(n, l) + 0.7, 0.7, 1.0);
    c *= diffuse;
  #endif
  float a = max(c.r, max(c.g, c.b));
  gl_FragColor = vec4(c, a);
}`;

const SPLAT_FRAG_SRC = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
uniform sampler2D uTarget;
uniform float aspectRatio;
uniform vec3 color;
uniform vec2 point;
uniform float radius;
void main () {
  vec2 p = vUv - point.xy;
  p.x *= aspectRatio;
  vec3 splat = exp(-dot(p, p) / radius) * color;
  vec3 base = texture2D(uTarget, vUv).xyz;
  gl_FragColor = vec4(base + splat, 1.0);
}`;

const ADVECTION_FRAG_SRC = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
uniform sampler2D uVelocity;
uniform sampler2D uSource;
uniform vec2 texelSize;
uniform vec2 dyeTexelSize;
uniform float dt;
uniform float dissipation;
vec4 bilerp (sampler2D sam, vec2 uv, vec2 tsize) {
  vec2 st = uv / tsize - 0.5;
  vec2 iuv = floor(st);
  vec2 fuv = fract(st);
  vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);
  vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);
  vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);
  vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);
  return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);
}
void main () {
  #ifdef MANUAL_FILTERING
    vec2 coord = vUv - dt * bilerp(uVelocity, vUv, texelSize).xy * texelSize;
    vec4 result = bilerp(uSource, coord, dyeTexelSize);
  #else
    vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
    vec4 result = texture2D(uSource, coord);
  #endif
  float decay = 1.0 + dissipation * dt;
  gl_FragColor = result / decay;
}`;

const DIVERGENCE_FRAG_SRC = `
precision mediump float;
precision mediump sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D uVelocity;
void main () {
  float L = texture2D(uVelocity, vL).x;
  float R = texture2D(uVelocity, vR).x;
  float T = texture2D(uVelocity, vT).y;
  float B = texture2D(uVelocity, vB).y;
  vec2 C = texture2D(uVelocity, vUv).xy;
  if (vL.x < 0.0) { L = -C.x; }
  if (vR.x > 1.0) { R = -C.x; }
  if (vT.y > 1.0) { T = -C.y; }
  if (vB.y < 0.0) { B = -C.y; }
  float div = 0.5 * (R - L + T - B);
  gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
}`;

const CURL_FRAG_SRC = `
precision mediump float;
precision mediump sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D uVelocity;
void main () {
  float L = texture2D(uVelocity, vL).y;
  float R = texture2D(uVelocity, vR).y;
  float T = texture2D(uVelocity, vT).x;
  float B = texture2D(uVelocity, vB).x;
  float vorticity = R - L - T + B;
  gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
}`;

const VORTICITY_FRAG_SRC = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
uniform sampler2D uVelocity;
uniform sampler2D uCurl;
uniform float curl;
uniform float dt;
void main () {
  float L = texture2D(uCurl, vL).x;
  float R = texture2D(uCurl, vR).x;
  float T = texture2D(uCurl, vT).x;
  float B = texture2D(uCurl, vB).x;
  float C = texture2D(uCurl, vUv).x;
  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
  force /= length(force) + 0.0001;
  force *= curl * C;
  force.y *= -1.0;
  vec2 velocity = texture2D(uVelocity, vUv).xy;
  velocity += force * dt;
  velocity = min(max(velocity, -1000.0), 1000.0);
  gl_FragColor = vec4(velocity, 0.0, 1.0);
}`;

const PRESSURE_FRAG_SRC = `
precision mediump float;
precision mediump sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D uPressure;
uniform sampler2D uDivergence;
void main () {
  float L = texture2D(uPressure, vL).x;
  float R = texture2D(uPressure, vR).x;
  float T = texture2D(uPressure, vT).x;
  float B = texture2D(uPressure, vB).x;
  float divergence = texture2D(uDivergence, vUv).x;
  float pressure = (L + R + B + T - divergence) * 0.25;
  gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
}`;

const GRADIENT_SUB_FRAG_SRC = `
precision mediump float;
precision mediump sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D uPressure;
uniform sampler2D uVelocity;
void main () {
  float L = texture2D(uPressure, vL).x;
  float R = texture2D(uPressure, vR).x;
  float T = texture2D(uPressure, vT).x;
  float B = texture2D(uPressure, vB).x;
  vec2 velocity = texture2D(uVelocity, vUv).xy;
  velocity.xy -= vec2(R - L, T - B);
  gl_FragColor = vec4(velocity, 0.0, 1.0);
}`;
